import { zodResolver } from '@hookform/resolvers/zod';
import type { HTMLProps } from 'react';
import type { DefaultValues } from 'react-hook-form';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Accordion } from '~/components/Accordion';
import { FormFieldDropdown, FormFieldInput } from '~/components/FormField';
import { useIsExternalUser } from '~/hooks/useIsExternalUser';
import { cn } from '~/lib/cn';
import { getI18nErrorMap } from '~/lib/getI18nErrorMap';
import { useOrganizationList } from '~/modules/settings/api/queries/organizationList';

import { useOktaUserData } from './hooks/useOktaUserData';
import { ExternalPermissions } from './parts/ExternalPermissions';
import { InternalPermissions } from './parts/InternalPermissions';
import {
  type UserFormSchema,
  userFormSchema,
  type UserFormSchemaForExternalUser,
  userFormSchemaForExternalUser,
} from './schema';

const USER_TYPES = [
  { value: 'internal', label: 'Internal' },
  { value: 'external', label: 'External' },
];

type FormType<TExternal extends boolean> = TExternal extends true
  ? UserFormSchemaForExternalUser
  : UserFormSchema;

type UserFormProps<TExternal extends boolean = false> = {
  isExternal?: TExternal;
  values?: FormType<TExternal>;
  onSubmit: (data: FormType<TExternal>) => void;
} & Omit<HTMLProps<HTMLFormElement>, 'method' | 'onSubmit'>;

export function UserForm<TExternal extends boolean = false>({
  values,
  onSubmit,
  className,
  ...formProps
}: UserFormProps<TExternal>) {
  const { t } = useTranslation('settings', { keyPrefix: 'user' });
  const isExternal = useIsExternalUser();
  const { organizationList } = useOrganizationList({ enabled: !isExternal });

  const form = useForm<FormType<TExternal>>({
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      jobTitle: '',
      userType: values?.userType ?? 'external',
      // Internal permissions should be hidden for external user
      ...(isExternal
        ? {}
        : {
            internalPermissions: {},
          }),

      // External permissions must be present for both internal and external
      externalPermissions: [
        {
          role: '',
          labels: [],
        },
      ],
    } as DefaultValues<
      TExternal extends true ? UserFormSchemaForExternalUser : UserFormSchema
    >,
    values,
    resolver: zodResolver(
      isExternal ? userFormSchemaForExternalUser : userFormSchema,
      {
        errorMap: getI18nErrorMap((s) => t(`validations.${s}`)),
      },
    ),
    mode: 'onBlur',
  });

  const { watch, reset, getValues } = form;

  const isEditMode = !!values;

  const { readOnlyFields } = useOktaUserData(form, !isEditMode);

  const selectedUserType = isEditMode
    ? values?.userType
    : // Path gets broken with TExternal generic
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      watch('userType' as any);

  const handleUserTypeChange = () => {
    reset({
      ...getValues(),
      organization: '',
      internalPermissions: {
        roles: [],
        labelGroups: [],
      },
      externalPermissions: [
        {
          role: '',
          labels: [],
        },
      ],
    });
  };

  return (
    <FormProvider {...form}>
      <form
        aria-label="user form"
        onSubmit={form.handleSubmit(onSubmit)}
        className={cn('flex', 'flex-col', 'gap-md', className)}
        {...formProps}
      >
        <Accordion defaultValue={['basicInfo', 'userInfo', 'userPermissions']}>
          <Accordion.Item
            value="basicInfo"
            title={t('basicInformationTitle')}
            className="grid grid-cols-3 gap-md bg-level-02"
          >
            <FormFieldInput
              name="firstName"
              label={t('fields.firstName')}
              placeholder={t('placeholders.firstName')}
              readOnly={isEditMode || readOnlyFields.includes('firstName')}
            />
            <FormFieldInput
              name="lastName"
              label={t('fields.lastName')}
              placeholder={t('placeholders.lastName')}
              readOnly={isEditMode || readOnlyFields.includes('lastName')}
            />
            <FormFieldInput
              name="email"
              type="email"
              label={t('fields.email')}
              placeholder={t('placeholders.email')}
              readOnly={isEditMode}
            />
            <FormFieldInput
              name="phone"
              type="tel"
              label={t('fields.phone')}
              placeholder={t('placeholders.phone')}
              readOnly={readOnlyFields.includes('phone')}
            />
            {isExternal ? (
              <FormFieldInput
                name="jobTitle"
                label={t('fields.jobTitle')}
                placeholder={t('placeholders.jobTitle')}
              />
            ) : null}
          </Accordion.Item>
          {!isExternal && (
            <Accordion.Item
              value="userInfo"
              title={t('userInformationTitle')}
              className="grid grid-cols-3 gap-md bg-level-02"
            >
              <FormFieldInput
                name="jobTitle"
                label={t('fields.jobTitle')}
                placeholder={t('placeholders.jobTitle')}
              />
              <FormFieldDropdown
                options={USER_TYPES}
                name="userType"
                label={t('fields.userType')}
                placeholder={t('placeholders.userType')}
                onChange={() => handleUserTypeChange()}
                readOnly
              />
              {selectedUserType === 'internal' ? (
                <FormFieldDropdown
                  options={
                    organizationList?.map((org) => ({
                      label: org.title,
                      value: org.id,
                    })) ?? []
                  }
                  name="organization"
                  label={t('fields.organization')}
                  placeholder={t('placeholders.organization')}
                />
              ) : null}
            </Accordion.Item>
          )}
          <Accordion.Item
            value="userPermissions"
            title={t('permissionsTitle')}
            className="flex flex-col gap-md bg-level-02"
          >
            <p className="text-subtle">{t('roleLabelNotice')}</p>
            {selectedUserType === 'internal' ? (
              <InternalPermissions
                name="internalPermissions"
                isEditing={isEditMode}
              />
            ) : (
              <ExternalPermissions
                name="externalPermissions"
                isEditing={isEditMode}
              />
            )}
          </Accordion.Item>
        </Accordion>
      </form>
    </FormProvider>
  );
}

UserForm.displayName = 'UserForm';
